import { JitsiConference, JitsiMeet, JitsiParticipant } from "lib/lib-jitsi-meet";
import { without } from "lodash-es";
import { action, computed, observable } from "mobx";
import { DataMessage, PerformanceSceneMessage, PerformerIdMessage } from "../../chat";
import { LocalUserState, PerformanceState } from "../../common/state";
import AbstractDataComponent from "../data/AbstractDataComponent";

export interface PerformerMessagesManagerProps {
    jitsi: JitsiMeet;
    room: JitsiConference;
    performance: PerformanceState;
    localUser: LocalUserState;
}

export default class extends AbstractDataComponent<PerformerMessagesManagerProps> {
    @computed get performerDisplayNames(): string[] {
        const userIds = [...this.props.performance.userLayout.values()];
        return userIds.map(
            (userId) => this.props.room.getParticipantById(userId)?.getDisplayName() ?? this.props.localUser.displayName
        );
    }

    public componentDidMount() {
        const { jitsi, room } = this.props;
        room.on(jitsi.events.conference.ENDPOINT_MESSAGE_RECEIVED, this.handleDataMessageReceived);
        room.on(jitsi.events.conference.MESSAGE_RECEIVED, this.handleMessageReceived);
    }

    public componentWillUnmount() {
        const { jitsi, room } = this.props;
        room.off(jitsi.events.conference.ENDPOINT_MESSAGE_RECEIVED, this.handleDataMessageReceived);
        room.off(jitsi.events.conference.MESSAGE_RECEIVED, this.handleMessageReceived);
    }

    @action
    private handleDataMessageReceived = (participant: JitsiParticipant, message: DataMessage) => {
        const { performance } = this.props;
        const userId = participant.getId();

        switch (message.kind) {
            case "performerId":
                const { id } = message as PerformerIdMessage;
                performance.userLayout.set(id, userId);
                break;
            case "performanceScene":
                const { scene } = message as PerformanceSceneMessage;
                performance.scene = scene;
                break;
        }
    };

    @action
    private handleMessageReceived = (_userId: string, message: string) => {
        const { performance } = this.props;
        let numMentionsInThisMessage = 0;
        let otherMentionInThisMessage: string | undefined;

        for (const name of this.performerDisplayNames) {
            if (message.toLowerCase().indexOf(name.toLowerCase()) >= 0) {
                const mentions = this.getSceneMentions();
                mentions.add(name);
                otherMentionInThisMessage = name;
                numMentionsInThisMessage += 1;

                const { advanceWithMentions } = performance.scenePrompts.get(performance.scene) ?? {};
                if (
                    (advanceWithMentions === "all" && mentions.size === performance.userLayout.size) ||
                    (advanceWithMentions === "duo" && numMentionsInThisMessage === 2) ||
                    advanceWithMentions === "any"
                ) {
                    if (advanceWithMentions === "duo") {
                        // assemble scene duos
                        performance.sceneDuos = [
                            [otherMentionInThisMessage, name],
                            without(this.performerDisplayNames, otherMentionInThisMessage, name) as [string, string],
                        ];
                    }

                    // all performers were mentioned
                    this.props.performance.scene += 1;
                    // this.updateRoomScene();
                    return;
                }
            }

            otherMentionInThisMessage = undefined;
        }
    };

    private getSceneMentions(): Set<string> {
        const { performance } = this.props;

        if (!performance.sceneMentionedUsers.has(performance.scene)) {
            performance.sceneMentionedUsers.set(performance.scene, observable(new Set<string>()));
        }

        return performance.sceneMentionedUsers.get(performance.scene)!;
    }

    // HACKHACK: copied from PerformanceStage
    private updateRoomScene = () => {
        const { room, performance } = this.props;
        const message: PerformanceSceneMessage = {
            kind: "performanceScene",
            scene: performance.scene,
        };
        for (const userId of Object.keys(room.participants)) {
            room.sendMessage(message, userId);
        }
    };
}
